import { DestroyRef, inject } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { endsWith, trimEnd } from 'lodash'

import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { HttpResponse } from '@angular/common/http'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { TableColumnPreferences } from '@shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { User } from '@app/core/model/user.model'
import { getIntlCurrencyDisplay } from '@shared/constants/currency-display.const'

export function isArrayOfStrings(value: any): boolean {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

export function enumToString(value: any, enumClass: any) {
  if (typeof value == 'string') {
    return value
  }
  return enumClass[value]
}

export function untilDestroyed() {
  const subject = new Subject()

  inject(DestroyRef).onDestroy(() => {
    subject.next(true)
    subject.complete()
  })

  return <T>() => takeUntil<T>(subject.asObservable())
}

export function privilegeFilter(user: User, array: MenuOptions[]) {
  return array.filter(function (item) {
    if (item.hasPrivilege) {
      return user.hasPrivilege(item.hasPrivilege)
    }

    if (item.hasFeature) {
      return user.company.scopePlan.hasFeature(item.hasFeature)
    }

    return true
  })
}

export function authorisedAsChildCompany() {
  return localStorage.getItem('globalParentChildAccountSwitcher-authorisedAsChildCompany')
}

export function setAuthorisedAsChildCompany(id: number | null) {
  return id === null
    ? localStorage.removeItem('globalParentChildAccountSwitcher-authorisedAsChildCompany')
    : localStorage.setItem('globalParentChildAccountSwitcher-authorisedAsChildCompany', id?.toString())
}

export function isValidEmail(email) {
  const EMAIL_REGEXP =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return EMAIL_REGEXP.test(email)
}

export function formatMonetaryValue(value: any, defaultValue?: string): string {
  if (!value) {
    return defaultValue || '-'
  }

  if (typeof value === 'string') {
    return String(value)
  }

  if (!value.currency) {
    return '-'
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: value.currency,
    currencyDisplay: getIntlCurrencyDisplay(value.currency),
  }).format(value.amount)
}

function parseHours(hours) {
  if (hours == null) return null

  const floor = Math.floor(hours)
  const remaining = hours - floor
  const format = { hours: parseInt(String(floor)), minutes: null }
  if (remaining == 0) {
    format.minutes = null
  } else if (remaining <= 0.25) {
    format.minutes = 15
  } else if (remaining <= 0.5) {
    format.minutes = 30
  } else if (remaining <= 0.75) {
    format.minutes = 45
  }

  return format
}

export function formatHours(hours, appendText = '') {
  const format = parseHours(hours)
  if (format.minutes == null) {
    return `${format.hours}${appendText}`
  }
  return `${format.hours}:${format.minutes}${appendText}`
}

export function formatDisplayHoursToMinutes(val) {
  let hours = val
  let minutes = '0'
  if (val.includes(':')) {
    const reg = new RegExp('(.*):(.*)').exec(val)
    hours = reg[1]
    minutes = reg[2]
  }
  return parseInt(hours) * 60 + parseInt(minutes)
}

export function isNonTradedDeliverable(deliverable: Deliverable) {
  return deliverable.deliveryStatus !== 'TRADED' && deliverable.deliveryStatus !== 'TRADING_FROM_IN_PROGRESS'
}

export function displayCurrencyValueNearestThousand(value: number) {
  if (value) {
    let isThousands
    let result = value.toFixed(2)
    if (result.length > 0) {
      const majors = result.substr(0, result.length - 3)
      result = majors.substr(0, majors.length - 3)
      isThousands = result.length > 0
      if (!isThousands) {
        result = majors
      }
    }
    result = result + (isThousands ? 'k' : '')
    return result
  }
  return '--'
}

export function getCurrencySymbol(currency: string) {
  if (!currency) {
    return null
  }
  return (0)
    .toLocaleString('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim()
}

export function trackById(_: number, item: any) {
  return item?.id
}

export const getPlainHttpParams = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== null && v !== undefined)) as Record<string, any>

export function downloadFile(res: HttpResponse<Blob>) {
  if (res.body != null) {
    const a = document.createElement('a')
    const fileName = res.headers.get('content-disposition')
    a.href = URL.createObjectURL(res.body)
    if (fileName != null) {
      a.download = fileName.split('filename=')[1].split(';')[0]
    }
    a.click()
  }
}

export function asMap(array, mapByProp = null, keyFunc = null, valFunc = null) {
  const map = {}
  array.forEach(function (a) {
    let key = a
    if (mapByProp) {
      const rawKey = a[mapByProp]
      key = keyFunc ? keyFunc(rawKey) : rawKey
    }
    let val = a
    if (valFunc) {
      val = valFunc(a)
    }
    map[key] = val
  })
  return map
}

export function mergePreferencesWithTableConfig(
  baseConfig: TableColumnPreferences,
  userPreferences: TableColumnPreferences
): TableColumnPreferences {
  const mergedConfig: TableColumnPreferences = { ...baseConfig }

  Object.keys(baseConfig).forEach((key) => {
    if (userPreferences?.[key]) {
      mergedConfig[key] = { ...baseConfig[key], selected: userPreferences[key].selected }
    }
  })

  return mergedConfig
}

export function generateFieldId(value: string, maxLength?: number): string {
  let fieldId = value.replace(/[^a-zA-Z\d_.]+/g, '_')
  let match = fieldId.match(/(\d*)$/)
  if (match?.[0] && !fieldId.includes('_')) {
    fieldId = fieldId.replace(/(\d*)$/, '_' + match[0])
  }
  match = fieldId.match(/^[^a-zA-Z_]/)
  if (match?.[0]) {
    fieldId = fieldId.replace(/^[^a-zA-Z_]/, '_' + match[0])
  }
  fieldId = maxLength ? fieldId.substring(0, maxLength) : fieldId
  return maxLength && endsWith(fieldId, '_') ? trimEnd(fieldId, '_') : fieldId
}

export function isPropertyOverridden(el: any, property: string): boolean {
  return (el.children || []).some((child: any) => {
    const style = String(child.attributes?.style || '')
    return style.includes(`${property}:`) || isPropertyOverridden(child, property)
  })
}

export function extractPropertyNames(elementStyle: string): string[] {
  return elementStyle
    .split(';')
    .map((decl) => decl.split(':')[0].trim())
    .filter(Boolean)
}

export function cleanElementStyle(elementStyle: string): string {
  return elementStyle
    .replace(/;;+/g, ';') // collapse double semicolons
    .replace(/^;|;$/g, '') // trim leading/trailing semicolons
    .trim()
}
